import { refreshAccessToken } from '@/shared/auth/auth.reducer';
import { getAccessToken, isAccessTokenExpiredOrStale, } from '@/shared/auth/jwt-storage';
import { store } from '@/store/store';
import { createCrmSockJsWebSocket, readStompConnectHeaders } from './crmStompTransport';
/** Refresh access token this long before expiry on each STOMP (re)connect. */
const STOMP_TOKEN_REFRESH_BUFFER_MS = 30_000;
export function createCrmStompLifecycleRefs() {
    return {
        stopped: { current: false },
        failureCount: { current: 0 },
        authRefreshInFlight: { current: false },
    };
}
/** Fresh JWT on every SockJS handshake (query param + STOMP CONNECT headers). */
export function createCrmStompWebSocketFactory(sockJsBaseUrl, onBeforeConnect) {
    return () => {
        onBeforeConnect?.();
        return createCrmSockJsWebSocket(sockJsBaseUrl);
    };
}
export async function refreshIamTokenForStomp(refs) {
    if (refs.authRefreshInFlight.current) {
        return Boolean(getAccessToken()) && !isAccessTokenExpiredOrStale(0);
    }
    refs.authRefreshInFlight.current = true;
    try {
        await store.dispatch(refreshAccessToken()).unwrap();
        return Boolean(getAccessToken());
    }
    catch {
        return false;
    }
    finally {
        refs.authRefreshInFlight.current = false;
    }
}
/**
 * Rotate access token before SockJS/STOMP handshake when JWT is expired or near expiry.
 * Used from STOMP `beforeConnect` so auto-reconnect does not reuse a stale `?access_token=`.
 */
export async function ensureFreshAccessTokenForStomp(refs) {
    if (!isAccessTokenExpiredOrStale(STOMP_TOKEN_REFRESH_BUFFER_MS)) {
        return Boolean(getAccessToken());
    }
    return refreshIamTokenForStomp(refs);
}
export function reconnectCrmStompClient(client, refs) {
    refs.stopped.current = false;
    refs.failureCount.current = 0;
    client.reconnectDelay = 5000;
    client.connectHeaders = readStompConnectHeaders();
    void client.deactivate().then(() => client.activate()).catch(() => undefined);
}
export function attachCrmStompTokenLifecycle(client, refs) {
    const onTokenUpdated = () => {
        // Skip when STOMP initiated the refresh — caller reconnects or beforeConnect continues.
        if (refs.authRefreshInFlight.current)
            return;
        reconnectCrmStompClient(client, refs);
    };
    window.addEventListener('callcenter-iam-tokens-updated', onTokenUpdated);
    return () => window.removeEventListener('callcenter-iam-tokens-updated', onTokenUpdated);
}
/**
 * On STOMP/SockJS auth failure: rotate access token once, then reconnect with the new JWT.
 * SockJS `/info` cannot reuse a URL that still carries an expired `access_token` query param.
 */
export async function handleCrmStompAuthFailure(client, refs, onStillFailed) {
    if (refs.authRefreshInFlight.current)
        return;
    const ok = await refreshIamTokenForStomp(refs);
    if (ok) {
        reconnectCrmStompClient(client, refs);
        return;
    }
    onStillFailed('STOMP auth rejected — shared connection stopped (token refresh failed).');
}
